import { useRef, useState } from "react";
import BookingFormCN from "@/components/BookingFormCN";
import PackageCardCN from "@/components/PackageCardCN";
import { PACKAGES_CN } from "@/types/booking-cn";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import heroImage from "@/assets/hero-balloon.jpg";
import heroVideo from "@/assets/hero-video.mp4";
import gallery1 from "@/assets/gallery/gallery-1.jpg";
import gallery2 from "@/assets/gallery/gallery-2.jpg";
import gallery3 from "@/assets/gallery/gallery-3.jpg";
import gallery4 from "@/assets/gallery/gallery-4.jpg";
import gallery5 from "@/assets/gallery/gallery-5.jpg";
import gallery6 from "@/assets/gallery/gallery-6.jpg";
import gallery7 from "@/assets/gallery/gallery-7.jpg";
import gallery8 from "@/assets/gallery/gallery-8.jpg";
import gallery9 from "@/assets/gallery/gallery-9.jpg";
import gallery10 from "@/assets/gallery/gallery-10.jpg";
import paymentMethods from "@/assets/payment-methods.png";
import aboutImage from "@/assets/about-image.jpg";

const IndexCN = () => {
  const [selectedPackageId, setSelectedPackageId] = useState<string>("");
  const bookingFormRef = useRef<HTMLDivElement>(null);

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackageId(packageId);
    bookingFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="py-3 px-4 w-full bg-transparent absolute top-0 left-0 right-0 z-10">
        <div className="container mx-auto flex items-center justify-center bg-transparent">
          <img
            alt="迪拜热气球之旅"
            className="h-10 md:h-12 lg:h-14 object-contain max-w-full"
            src="/lovable-uploads/63168f7f-6a03-4d9b-a252-442f3477b0b3.png"
          />
        </div>
      </header>

      {/* Hero Section with Booking Form */}
      <section className="relative min-h-screen flex items-start">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-16 pt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="max-w-md" ref={bookingFormRef}>
              <BookingFormCN selectedPackageId={selectedPackageId} />
            </div>
            <div className="max-w-2xl">
              <video
                className="w-full rounded-lg shadow-2xl"
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={heroVideo} type="video/mp4" />
                您的浏览器不支持视频标签。
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">套餐选择</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl">
            {PACKAGES_CN.map(pkg => (
              <PackageCardCN key={pkg.id} package={pkg} onBookNow={handlePackageSelect} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 px-4">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-white">
            <div>
              <div className="text-6xl font-bold mb-2">500+</div>
              <div className="text-lg">成功飞行次数</div>
            </div>
            <div>
              <div className="text-6xl font-bold mb-2">2000+</div>
              <div className="text-lg">满意客户</div>
            </div>
            <div>
              <div className="text-6xl font-bold mb-2">4.5</div>
              <div className="text-lg">星级评价</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 bg-secondary/10">
        <div className="container mx-auto">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">
            为什么成千上万的游客选择我们预订热气球之旅
          </h3>
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                您的天空
                <br />
                之门
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                我们是迪拜首屈一指的热气球体验预订平台。我们不仅仅是预订飞行；我们创造回忆。
                通过与该地区监管最严格、评价最高的运营商合作，我们确保您的旅程不仅令人叹为观止，
                而且符合最高安全标准。
              </p>
              <p className="text-muted-foreground font-medium mb-3">我们引以为豪的是：</p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>经过审核的运营商：我们只与持有GCAA执照的飞行员合作。</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>即时确认：无需等待，立即确保您的位置。</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>灵活取消：计划有变？享受无忧取消政策。</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>无隐藏费用：您看到的价格就是您支付的价格。</span>
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-6">
                与我们一起飞行，您不仅仅是乘坐热气球，更是体验一生难忘的冒险。
              </p>
            </div>
            <div>
              <img
                src={aboutImage}
                alt="女士在迪拜沙漠日出时享受热气球之旅"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 px-4 bg-cyan-50">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">精彩图集</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-6xl">
            <img src={gallery1} alt="热气球乘客欣赏沙漠美景" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery2} alt="绿白色热气球航拍" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery3} alt="绿色条纹热气球飞行中" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery4} alt="热气球篮中的集体照" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery5} alt="热气球篮的沙漠航拍" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery6} alt="家庭享受热气球之旅" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery7} alt="情侣庆祝热气球冒险" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery8} alt="热气球飞越沙漠" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery9} alt="迪拜沙漠多个热气球" className="rounded-lg w-full h-64 object-cover" />
            <img src={gallery10} alt="缤纷热气球飞行体验" className="rounded-lg w-full h-64 object-cover" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">常见问题 - 热气球冒险</h2>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                1. 迪拜热气球安全吗？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                绝对安全。我们只列出获得阿联酋民航总局（GCAA）许可的运营商。
                所有飞行员均获得国际认证，设备每天都经过严格检查。
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                2. 最佳预订时间是什么时候？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                热气球是清晨活动，可以观赏日出。我们建议在旺季（10月至3月）
                至少提前3-4天预订，因为名额很快就会被预订满。
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                3. 你们提供酒店接送吗？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                是的！我们所有的套餐，从标准到过夜套餐，都包括迪拜市区酒店的接送服务。
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                4. 儿童可以参加吗？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                可以，5岁以上的儿童可以参加。对于家庭来说，这是一次安全而神奇的体验。
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                5. 如果天气不好怎么办？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                您的安全是首要考虑。如果因风或雾取消飞行，您将获得全额退款
                或选择改期至下一个可用日期。
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                6. 我应该穿什么？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                穿着舒适保暖，尤其是清晨。运动装或休闲装搭配封闭式鞋子最为理想。
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                7. 我可以带手机或相机吗？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                当然可以！捕捉每一个精彩瞬间——只需确保飞行时您的设备系好安全带。
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                8. 如何联系我们？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                如有疑问，请联系我们：
                <br />
                <strong>邮箱：</strong>{" "}
                <a href="mailto:info@hotairballoonrideindubai.com" className="text-primary hover:underline">
                  info@hotairballoonrideindubai.com
                </a>
                <br />
                <strong>电话：</strong>{" "}
                <a href="tel:+971504209377" className="text-primary hover:underline">
                  +971 50 420 9377
                </a>
                {" | "}
                <strong>微信/WhatsApp：</strong>{" "}
                <a
                  href="https://wa.me/971504209377?text=Hello"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  +971 50 420 9377
                </a>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="border rounded-lg px-6 bg-card">
              <AccordionTrigger className="text-left font-semibold hover:no-underline">
                9. 取消和退款政策是什么？
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                取消必须通过与预订相同的方式进行。如果在72小时前取消，您将获得100%退款；
                如果在48小时前取消，将获得50%退款；24小时内取消不予退款。
                取消请联系{" "}
                <a href="mailto:info@hotairballoonrideindubai.com" className="text-primary hover:underline">
                  info@hotairballoonrideindubai.com
                </a>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-8 px-4 bg-secondary/10">
        <div className="container mx-auto text-center">
          <img
            src={paymentMethods}
            alt="接受的支付方式：Visa、Mastercard、Tabby、Google Pay、Apple Pay"
            className="mx-auto max-w-2xl w-full h-auto"
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-8 px-4">
        <div className="container mx-auto text-center">
          <p className="text-sm opacity-80">©2025 hotairballoonrideindubai.com. 版权所有。</p>
        </div>
      </footer>
    </div>
  );
};

export default IndexCN;
